/**
 * LogService - prosty system logowania z fingerprint urządzenia
 *
 * Identyfikacja:
 * - fingerprint (fp) - unikalny hash urządzenia
 * - sessionId (sid) - unikalny per sesja przeglądarki (sessionStorage)
 *
 * Użycie:
 * LogService.init(__CFG.primaryEndpoint);
 * LogService.log('wiadomość', obiekt, 'kolejna wiadomość');
 */

var LogService = (function() {

  'use strict';

  var _fingerprint = null;
  var _sessionId = null;
  var _endpoint = '';
  var _buffer = [];
  var _maxBufferSize = 50;
  var _flushInterval = null;
  var _failCount = 0;
  var _maxFails = 5;
  var _initialized = false;
  var _fingerprintReady = false;

  // ============================================
  // FINGERPRINT COLLECTOR
  // ============================================

  var FingerprintCollector = {

    collect: function(callback) {
      var self = this;
      var signals = {};
      var pending = 2;
      var completed = 0;

      var checkComplete = function() {
        completed++;
        if (completed >= pending) {
          callback(self.hashSignals(signals));
        }
      };

      // Synchroniczne
      var s = window.screen || {};
      var n = navigator || {};

      signals.screen = [s.width, s.height, s.colorDepth, window.devicePixelRatio].join('x');
      signals.navigator = [n.userAgent, n.language, n.platform, n.hardwareConcurrency, n.deviceMemory].join('|');
      signals.timezone = (function() {
        try { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
        catch(e) { return new Date().getTimezoneOffset(); }
      })();
      signals.plugins = (function() {
        var p = n.plugins || [];
        var names = [];
        for (var i = 0; i < Math.min(p.length, 5); i++) {
          if (p[i] && p[i].name) names.push(p[i].name);
        }
        return names.join('|');
      })();

      // Canvas
      this.getCanvasFingerprint(function(result) {
        signals.canvas = result;
        checkComplete();
      });

      // WebGL
      this.getWebGLInfo(function(result) {
        signals.webgl = result;
        checkComplete();
      });
    },

    getCanvasFingerprint: function(callback) {
      try {
        var canvas = document.createElement('canvas');
        canvas.width = 200;
        canvas.height = 50;
        var ctx = canvas.getContext('2d');

        if (!ctx) {
          callback('no-canvas');
          return;
        }

        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(10, 10, 100, 30);
        ctx.fillStyle = '#069';
        ctx.font = '14px Arial';
        ctx.fillText('FP Test 123', 15, 30);

        callback(this.simpleHash(canvas.toDataURL()));
      } catch (e) {
        callback('canvas-error');
      }
    },

    getWebGLInfo: function(callback) {
      try {
        var canvas = document.createElement('canvas');
        var gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');

        if (!gl) {
          callback('no-webgl');
          return;
        }

        var debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        var result = gl.getParameter(gl.VENDOR) + '|' + gl.getParameter(gl.RENDERER);

        if (debugInfo) {
          result += '|' + gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL);
        }

        callback(this.simpleHash(result));
      } catch (e) {
        callback('webgl-error');
      }
    },

    simpleHash: function(str) {
      var hash = 0;
      if (!str || str.length === 0) return '0';
      for (var i = 0; i < str.length; i++) {
        hash = ((hash << 5) - hash) + str.charCodeAt(i);
        hash = hash & hash;
      }
      return Math.abs(hash).toString(36);
    },

    hashSignals: function(signals) {
      var str = JSON.stringify(signals);
      var h1 = 0xdeadbeef;
      var h2 = 0x41c6ce57;

      for (var i = 0; i < str.length; i++) {
        var ch = str.charCodeAt(i);
        h1 = Math.imul(h1 ^ ch, 2654435761);
        h2 = Math.imul(h2 ^ ch, 1597334677);
      }

      h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507) ^ Math.imul(h2 ^ (h2 >>> 13), 3266489909);
      h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507) ^ Math.imul(h1 ^ (h1 >>> 13), 3266489909);

      return (h2 >>> 0).toString(16) + (h1 >>> 0).toString(16);
    }
  };

  // ============================================
  // GŁÓWNY SERWIS
  // ============================================

  return {

    init: function(endpoint) {
      if (_initialized) return this;

      var self = this;
      _endpoint = endpoint || '';

      // Session ID - nowy per sesja przeglądarki
      _sessionId = sessionStorage.getItem('log_sid');
      if (!_sessionId) {
        _sessionId = 'ses_' + Date.now().toString(36) + '_' + Math.random().toString(36).substr(2, 9);
        sessionStorage.setItem('log_sid', _sessionId);
      }

      // Fingerprint
      if (window.fingerprint) {
        _fingerprint = window.fingerprint;
        _fingerprintReady = true;
      } else {
        FingerprintCollector.collect(function(hash) {
          _fingerprint = hash;
          _fingerprintReady = true;
          window.fingerprint = hash;
        });
      }

      // Auto-flush co 5s
      _flushInterval = setInterval(function() { self.flush(); }, 5000);

      // Flush przed zamknięciem
      var flushBeacon = function() { self.flush(true); };
      document.addEventListener('visibilitychange', function() {
        if (document.visibilityState === 'hidden') flushBeacon();
      }, true);
      window.addEventListener('pagehide', flushBeacon, true);
      window.addEventListener('beforeunload', flushBeacon, true);

      _initialized = true;
      return this;
    },

    /**
     * Logowanie - przyjmuje dowolną liczbę argumentów (jak console.log)
     */
    log: function() {
      var args = Array.prototype.slice.call(arguments);
      var msg = [];

      for (var i = 0; i < args.length; i++) {
        var a = args[i];
        msg.push(typeof a === 'string' ? a : JSON.stringify(a));
      }

      var now = new Date();
      var ts = now.getTime();
      var time = now.toISOString().replace('T', ' ').substr(0, 19);

      var entry = {
        ts: ts,
        time: time,
        msg: msg.join(' >>> ')
      };

      // Kontekst w pierwszym logu bufora
      if (_buffer.length === 0) {
        entry.ctx = {
          fp: _fingerprint || window.fingerprint || 'pending',
          sid: _sessionId,
          ua: navigator.userAgent,
          url: window.location.href
        };
      }

      _buffer.push(entry);

      if (_buffer.length >= _maxBufferSize) {
        this.flush();
      }

      return this;
    },

    flush: function(useBeacon) {
      var self = this;

      if (_buffer.length === 0 || _failCount >= _maxFails) return this;
      if (!_fingerprintReady && !window.fingerprint) return this;

      var payload = {
        fp: _fingerprint || window.fingerprint || 'unknown',
        sid: _sessionId,
        logs: _buffer.slice()
      };

      _buffer = [];

      var url = _endpoint + '/log';
      var body = JSON.stringify(payload);

      if (useBeacon) {
        if (!navigator.sendBeacon(url, body)) {
          _buffer = payload.logs.concat(_buffer).slice(0, _maxBufferSize);
        }
        return this;
      }

      fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: body,
        keepalive: true
      })
      .then(function(response) {
        if (!response.ok) throw new Error('HTTP ' + response.status);
        _failCount = 0;
      })
      .catch(function() {
        _failCount++;
        if (payload.logs.length < 100) {
          _buffer = payload.logs.concat(_buffer).slice(0, _maxBufferSize);
        }
      });

      return this;
    },

    getFingerprint: function() { return _fingerprint; },
    getSessionId: function() { return _sessionId; }
  };

})();

if (typeof module !== 'undefined' && module.exports) {
  module.exports = LogService;
}

export default LogService;



// WEBPACK FOOTER //
// ./src/logService.js